/**
 * Canonical Visual Designer summary rows. Every output that describes a Visual Designer item reads these rows.
 * Pure module: no React, UI or service imports, because scripts/sync-edge-shared.js copies it into
 * supabase/functions/_shared for the edge functions.
 */
import { computeBayGeometry, createDefaultBayConfig, normalizeBayConfig } from '../../../utils/bayGeometry';
import {
  describeSlidingPanels,
  doorKindUsesHingePlacement,
  doorKindUsesOpenDirection,
  formatMasterLeaf,
  formatStackDirection,
  formatTrafficDoor,
  formatThreshold,
  getDoorKind,
  isDoorType,
} from './doorTypes';
import { formatGlassSpecification } from './glassSpecification';
import { formatTrickleVentsPositionLabel } from './trickleVentPosition';
import { EXTENDERS_MM_OUTPUT_LABEL, formatExtendersMmFromOptions } from './visualDesignerFieldFormatters';

export const WINDOW_GENERATOR_PX2MM = 2.5;

/** Frame colours aligned with Window Generator FRAME_COLS (same names and hex). */
export const FRAME_COLOUR_OPTIONS: Array<{ code: string; label: string }> = [
  { code: 'white-smooth', label: 'White Smooth' },
  { code: 'white-wood-grain', label: 'White Wood Grain' },
  { code: 'agate-grey', label: 'Agate Grey' },
  { code: 'black-noir', label: 'Black Noir' },
  { code: 'anthracite-grey-grain', label: 'Anthracite Grey Grain' },
  { code: 'anthracite-smooth', label: 'Anthracite Smooth' },
  { code: 'anthracite', label: 'Anthracite' },
  { code: 'grey', label: 'Grey' },
  { code: 'black', label: 'Black' },
  { code: 'chartwell-green', label: 'Chartwell Green' },
  { code: 'cream', label: 'Cream' },
  { code: 'golden-oak', label: 'Golden Oak' },
  { code: 'honey-oak', label: 'Honey Oak' },
  { code: 'irish-oak', label: 'Irish Oak' },
  { code: 'pebble-grey', label: 'Pebble Grey' },
  { code: 'rosewood', label: 'Rosewood' },
  { code: 'silver-grey', label: 'Silver Grey' },
  { code: 'natural-untreated', label: 'Natural/Untreated' },
  { code: 'primed', label: 'Primed' },
  { code: 'black-on-white-smooth', label: 'Black on White Smooth' },
  { code: 'agate-grey-on-white-smooth', label: 'Agate Grey on White Smooth' },
  { code: 'anthracite-on-white-smooth', label: 'Anthracite on White Smooth' },
];

/** Single-side frame colours (exclude pre-baked dual-colour combos). */
export const FRAME_SIDE_COLOUR_OPTIONS = FRAME_COLOUR_OPTIONS.filter((o) => !String(o.code).includes('-on-'));
export const CUSTOM_FRAME_COLOUR_CODE = 'ral-custom';

/** Registry fieldKey for each summary row (consumerContractRegistry.summaryFields). */
export type DesignerSummaryRowKey =
  | 'window_type_name'
  | 'profile_definition'
  | 'material_name'
  | 'bay_tangent_width'
  | 'height_mm'
  | 'width_height_size'
  | 'reference'
  | 'glass_type'
  | 'glass_specification'
  | 'variant'
  | 'beading_type'
  | 'frame_color_external'
  | 'frame_color_internal'
  | 'frame_color_combined'
  | 'handle_color'
  | 'handle_style'
  | 'hinge_placement'
  | 'open_direction'
  | 'master_leaf'
  | 'panel_count'
  | 'stack_direction'
  | 'traffic_door'
  | 'sliding_panels'
  | 'threshold'
  | 'trickle_vents'
  | 'cill_option'
  | 'extenders_mm'
  | 'georgian_bars'
  | 'astragal_bars'
  | 'bay_facet_widths'
  | 'bay_projection_depth'
  | 'bay_joint_angles'
  | 'window_hinge_type'
  | 'astragal_bar_side'
  | 'notes';

export type DesignerSummaryRow = { key: DesignerSummaryRowKey; label: string; value: string };

/** Loose shape of line meta_json read by the summary. */
export type DesignerSummaryMeta = {
  window_type?: { id?: string | null; name?: string | null } | null;
  window_material?: { name?: string | null } | null;
  window_style?: { category?: unknown; templatePayload?: any } | null;
  specs: {
    width_mm?: number | string | null;
    height_mm?: number | string | null;
    reference?: string | null;
    glass_type?: string | null;
    variant?: string | null;
    beading_type?: string | null;
    notes?: string | null;
    options?: Record<string, any> | null;
    [key: string]: any;
  };
};

export const formatProfileDefinition = (value: unknown): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.toLowerCase() === 'heritage' ? 'Heritage' : 'Standard';
};

export function isBayLikePayload(payload: any): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const rootPreset = String(payload?.root?.preset || '').trim().toLowerCase();
  return rootPreset === 'bay';
}

export { isDoorType };

export function getDesignerSummaryKeyedRows(meta: DesignerSummaryMeta): DesignerSummaryRow[] {
  const resolveFrameDisplayValues = () => {
    const specs: any = meta?.specs || {};
    const extCode = String(specs?.frame_color_code_external || '').trim();
    const intCode = String(specs?.frame_color_code_internal || '').trim();
    const extRaw = String(specs?.frame_color_external || '').trim();
    const intRaw = String(specs?.frame_color_internal || '').trim();
    const sharedRaw = String(specs?.frame_color || '').trim();
    const extPreset = FRAME_SIDE_COLOUR_OPTIONS.find((o) => o.code === extCode)?.label || '';
    const intPreset = FRAME_SIDE_COLOUR_OPTIONS.find((o) => o.code === intCode)?.label || '';
    const extLabel = (extCode && extCode !== CUSTOM_FRAME_COLOUR_CODE) ? (extPreset || extRaw || sharedRaw) : (extRaw || sharedRaw);
    const intLabel = (intCode && intCode !== CUSTOM_FRAME_COLOUR_CODE) ? (intPreset || intRaw || sharedRaw) : (intRaw || sharedRaw);
    const sharedLabel =
      extLabel && intLabel
        ? (extLabel === intLabel ? extLabel : `${extLabel} (external) / ${intLabel} (internal)`)
        : (extLabel || intLabel || sharedRaw);
    return { extLabel, intLabel, sharedLabel };
  };
  const deriveCillPlacement = (): string => {
    const o: any = meta.specs.options || {};
    const payload: any = (meta as any)?.window_style?.templatePayload;
    const isUnionShape = !!(payload?.root && payload.root.type === 'rectUnion' && Array.isArray(payload.root.regions) && payload.root.regions.length > 1);
    if (!isUnionShape) return '';
    const explicitLabel = String(o.cill_union_mode_label || '').trim();
    if (explicitLabel) return explicitLabel;
    const pres: any = payload?.presentation || {};
    const rawMode = String(o.cill_union_mode || pres?.cillUnionMode || pres?.cill_union_mode || '').trim().toLowerCase();
    const includeUpper = (o.cill_union_include_upper === true) || (pres?.cillUnionIncludeUpper === true);
    const normalized =
      rawMode === 'window_only' || rawMode === 'window-only' || rawMode === 'window only' ? 'window_only'
      : rawMode === 'both' || rawMode === 'door_window' || rawMode === 'door+window' ? 'both'
      : rawMode === 'door_only' || rawMode === 'door-only' || rawMode === 'door only' ? 'door_only'
      : (includeUpper ? 'both' : 'door_only');
    if (normalized === 'window_only') return 'Window Only';
    if (normalized === 'both') return 'Door & Window';
    return 'Door Only';
  };
  const windowTypeName = String((meta as any)?.window_type?.name || '').trim();
  const materialName = String((meta as any)?.window_material?.name || '').trim();
  const isDoor = isDoorType(meta?.window_type);
  const doorKind = getDoorKind(meta?.window_type);
  const rows: DesignerSummaryRow[] = [];
  const push = (key: DesignerSummaryRowKey, label: string, value: any) => {
    const v = (value == null ? '' : String(value)).trim();
    if (!v) return;
    rows.push({ key, label, value: v });
  };

  push('window_type_name', 'Window type', windowTypeName);
  push('profile_definition', 'Profile Definition', formatProfileDefinition((meta as any)?.window_style?.category));
  push('material_name', 'Material', materialName);

  const o: any = meta.specs.options || {};
  const templatePayload: any = (meta as any)?.window_style?.templatePayload;
  const deriveBayFromPayload = () => {
    if (!templatePayload?.root || templatePayload.w == null || templatePayload.h == null) return null;
    if (!isBayLikePayload(templatePayload)) return null;
    try {
      const fallback = createDefaultBayConfig({
        tangentWidthMm: Math.max(200, Math.round(Number(templatePayload.w || 400) * WINDOW_GENERATOR_PX2MM)),
        heightMm: Math.max(200, Math.round(Number(templatePayload.h || 300) * WINDOW_GENERATOR_PX2MM)),
      });
      const cfg: any = normalizeBayConfig(templatePayload?.presentation?.bayConfig || fallback, {
        tangentWidthMm: Math.max(200, Math.round(Number(templatePayload.w || 400) * WINDOW_GENERATOR_PX2MM)),
        heightMm: Math.max(200, Math.round(Number(templatePayload.h || 300) * WINDOW_GENERATOR_PX2MM)),
      });
      const geo: any = computeBayGeometry(cfg);
      const isCurvedBow = String(cfg?.bayType || '').trim().toUpperCase() === 'CURVED_BOW' || geo?.derived?.hasCurvedFace === true;
      const tangent = Math.round(Number(geo?.derived?.tangentWidth || cfg?.tangentWidth || 0)) || 0;
      const facetWidths = Array.isArray(cfg?.facets)
        ? cfg.facets.map((f: any) => Math.round(Number(f?.width || 0))).filter((n: number) => Number.isFinite(n) && n > 0)
        : [];
      const jointAngles = Array.isArray(geo?.joints)
        ? geo.joints
            .map((j: any) => Number(j?.manualInternalAngle ?? j?.derivedInternalAngle))
            .filter((n: number) => Number.isFinite(n))
            .map((n: number) => Math.round(n))
        : [];
      const projectionDepth = isCurvedBow ? (Math.round(Number(geo?.derived?.projection || cfg?.projectionDepth || 0)) || 0) : 0;
      return { tangent, isCurvedBow, projectionDepth, facetWidths, jointAngles };
    } catch {
      return null;
    }
  };
  const bayFromPayload = deriveBayFromPayload();
  const bayTangent = Number(o.bay_tangent_width_mm || bayFromPayload?.tangent || 0) || 0;
  const isCurvedBay = bayFromPayload?.isCurvedBow === true;
  const bayProjectionDepth = isCurvedBay ? (Number(o.bay_projection_depth_mm || bayFromPayload?.projectionDepth || 0) || 0) : 0;
  const isBay = isBayLikePayload(templatePayload);
  const w = meta.specs.width_mm ? `${meta.specs.width_mm}mm` : '';
  const h = meta.specs.height_mm ? `${meta.specs.height_mm}mm` : '';
  if (isBay) {
    push('bay_tangent_width', 'Tangent width', bayTangent ? `${bayTangent}mm` : w);
    push('height_mm', 'Height', h);
  } else {
    const dim = [w, h].filter(Boolean).join(' × ');
    push('width_height_size', 'Size', dim);
  }
  push('reference', 'Reference', meta.specs.reference || '');
  push('glass_type', 'Glass', meta.specs.glass_type || '');
  push('glass_specification', 'Glass Specification', formatGlassSpecification(o.glass_specification));
  push('variant', 'Profile type', meta.specs.variant || '');
  push('beading_type', 'Beading', meta.specs.beading_type || '');
  const frameDisplay = resolveFrameDisplayValues();
  const externalColour = frameDisplay.extLabel;
  const internalColour = frameDisplay.intLabel;
  const sharedColour = frameDisplay.sharedLabel;
  if (externalColour || internalColour) {
    // Keep side-specific rows visible even when both sides currently share the same value.
    push('frame_color_external', 'External colour / finish', externalColour || sharedColour);
    push('frame_color_internal', 'Internal colour / finish', internalColour || sharedColour);
  } else {
    push('frame_color_combined', 'Colour / finish', sharedColour);
  }

  push('handle_color', 'Handle colour', o.handle_color || '');
  push('handle_style', 'Handle style', o.handle_style || '');
  if (doorKindUsesHingePlacement(doorKind) && o.hinge_placement) push('hinge_placement', 'Hinge', o.hinge_placement === 'left' ? 'Left' : 'Right');
  if (doorKindUsesOpenDirection(doorKind) && o.open_direction) {
    push('open_direction', 'Opens', o.open_direction === 'in' ? 'In' : o.open_direction === 'bi-directional' ? 'Bi-directional' : 'Out');
  }
  if (doorKind === 'french') push('master_leaf', 'Master leaf', formatMasterLeaf(o.master_leaf));
  if ((doorKind === 'bifold' || doorKind === 'slider') && Number(o.panel_count) > 0) {
    push('panel_count', 'Panels', String(Math.round(Number(o.panel_count))));
  }
  if (doorKind === 'bifold') {
    push('stack_direction', 'Stack', formatStackDirection(o.stack_direction));
    push('traffic_door', 'Traffic door', formatTrafficDoor(o.traffic_door));
  }
  if (doorKind === 'slider') push('sliding_panels', 'Sliding', describeSlidingPanels(templatePayload));
  if (isDoor) push('threshold', 'Threshold?', formatThreshold(o.threshold));
  if (o.trickle_vents === true) {
    push('trickle_vents', 'Trickle vents', formatTrickleVentsPositionLabel(o.trickle_vents_position));
  } else if (o.trickle_vents === false) {
    push('trickle_vents', 'Trickle vents', 'No');
  }
  const cillOpt = o.cill_option;
  const cillPlacement = deriveCillPlacement();
  const cill =
    cillOpt === '150' ? `150mm${cillPlacement ? ` (${cillPlacement})` : ''}`
    : cillOpt === '180' ? `180mm${cillPlacement ? ` (${cillPlacement})` : ''}`
    : cillOpt === 'stub' ? `Stub${cillPlacement ? ` (${cillPlacement})` : ''}`
    : cillOpt === 'none' ? 'None'
    : '';
  push('cill_option', 'Cill', cill);

  const extLabel = formatExtendersMmFromOptions(o);
  if (extLabel) push('extenders_mm', EXTENDERS_MM_OUTPUT_LABEL, extLabel);

  if (o.georgian_bars === true) {
    const cols = Number(o.georgian_cols || 0) || 0;
    const rowsN = Number(o.georgian_rows || 0) || 0;
    const wmm = Number(o.georgian_width_mm || 0) || 0;
    const grid = cols && rowsN ? `${cols}×${rowsN}` : cols ? `${cols} cols` : rowsN ? `${rowsN} rows` : '';
    push('georgian_bars', 'Georgian', [grid, wmm ? `${wmm}mm` : ''].filter(Boolean).join(' '));
  }

  if (o.astragal_bars === true) {
    const cols = Number(o.astragal_cols || 0) || 0;
    const rowsN = Number(o.astragal_rows || 0) || 0;
    const wmm = Number(o.astragal_width_mm || 0) || 0;
    const grid = cols && rowsN ? `${cols}×${rowsN}` : cols ? `${cols} cols` : rowsN ? `${rowsN} rows` : '';
    push('astragal_bars', 'Astragal', [grid, wmm ? `${wmm}mm` : ''].filter(Boolean).join(' '));
  }
  const facetWidths: number[] = Array.isArray(o.bay_facet_widths_mm)
    ? (o.bay_facet_widths_mm as any[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
    : (bayFromPayload?.facetWidths || []);
  const jointAngles: number[] = Array.isArray(o.bay_joint_angles_deg)
    ? (o.bay_joint_angles_deg as any[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
    : (bayFromPayload?.jointAngles || []);
  if (isBay && facetWidths.length) push('bay_facet_widths', 'Facet widths (mm)', facetWidths.map((n) => Math.round(n)).join(', '));
  if (isBay && bayProjectionDepth) push('bay_projection_depth', 'Projection depth', `${Math.round(bayProjectionDepth)}mm`);
  if (isBay && jointAngles.length) push('bay_joint_angles', 'Joint angles (deg)', jointAngles.map((n) => `${Math.round(n)}°`).join(', '));

  {
    const htCode = String(o.window_hinge_type_code || o.hinge_type_code || '').trim().toLowerCase();
    const htLabel = String(o.window_hinge_type || o.hinge_type || '').trim();
    if (!isDoor) {
      const normalized =
        htLabel
          ? htLabel.replace(/\s*\(default\)\s*$/i, '')
          : htCode === 'egress'
            ? 'Egress'
            : htCode === 'ez_clean'
              ? 'EZ Clean'
              : htCode === 'fire_rated'
                ? 'Fire-Rated'
                : 'Standard';
      push('window_hinge_type', 'Hinge type', normalized);
    }
  }
  {
    const sideCode = String(o.astragal_bar_side || o.astragal_side || '').trim().toLowerCase();
    const sideLabel = String(o.astragal_bar_side_label || '').trim();
    const show =
      (sideCode && sideCode !== 'none' && sideCode !== 'both') ||
      (!!sideLabel && !/^(none|both)/i.test(sideLabel));
    if (show) push('astragal_bar_side', 'Astragal bar side', sideLabel || sideCode);
  }

  push('notes', 'Notes', meta.specs.notes || '');
  return rows;
}

/** Rows only supplier outputs and the Final Survey review may show. getDesignerSummaryRows leaves them out. */
export const SUPPLIER_ONLY_ROW_KEYS: DesignerSummaryRowKey[] = ['glass_specification'];

/** Rows for quote and customer outputs: every row except SUPPLIER_ONLY_ROW_KEYS. */
export function getDesignerSummaryRows(meta: DesignerSummaryMeta): Array<{ label: string; value: string }> {
  return getDesignerSummaryKeyedRows(meta)
    .filter(({ key }) => !SUPPLIER_ONLY_ROW_KEYS.includes(key))
    .map(({ label, value }) => ({ label, value }));
}

/** Summary rows written into a Visual Designer line's description, in this order. */
export const LINE_DESCRIPTION_ROW_KEYS: DesignerSummaryRowKey[] = [
  'reference',
  'width_height_size',
  'bay_tangent_width',
  'height_mm',
  'glass_type',
  'variant',
  'beading_type',
  'frame_color_external',
  'frame_color_internal',
  'frame_color_combined',
  'handle_color',
  'handle_style',
  'hinge_placement',
  'open_direction',
  'master_leaf',
  'panel_count',
  'stack_direction',
  'traffic_door',
  'sliding_panels',
  'threshold',
  'window_hinge_type',
  'trickle_vents',
  'cill_option',
];

export function buildVisualDesignerLineDescription(meta: DesignerSummaryMeta): string | null {
  const rows = getDesignerSummaryKeyedRows(meta);
  const parts = LINE_DESCRIPTION_ROW_KEYS.flatMap((key) => {
    const row = rows.find((r) => r.key === key);
    return row ? [`${row.label}: ${row.value}`] : [];
  });
  return parts.length ? parts.join(' • ') : null;
}
